import React, { useState, useEffect, useMemo } from 'react';
import { ChartDataService } from '../../services/ChartDataService';
import { useUnifiedAuth } from '../../auth/useUnifiedAuth';
import { useSAPDataStore } from '../../store/SAPDataStore';
import { useChartCache } from '../../hooks/useChartCache';
import BaseChart from './BaseChart';
import { useTranslation } from 'react-i18next';

// Expected formats:
// Nested:  { "2016-01-19 00:00:00 +0000 UTC": { "A1": { "fault": ms } } }
//          { "2016-01-19 00:00:00 +0000 UTC": { "A1": [{name, value}] } }
// Flat:    { "2015-01-20 B1": { "fault": ms } }
//          { "2015-01-20 B1": [{name, value}] }
type DowntimeDurationResults = Record<string, any>;

interface DowntimeDurationDataPoint {
  date: string;
  shift: string;
  fault: string;
  duration: number; // Duration in milliseconds
}

interface DowntimeDurationOfFaultsChartProps {
  machineSerialNo?: string;
  lineId?: string;
  shifts?: string[];
}

// Format milliseconds into a human-readable duration string
const formatDuration = (ms: number): string => {
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

// Format milliseconds for axis labels (compact)
const formatDurationAxis = (ms: number): string => {
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);
  if (hours === 0) return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}h`;
};

// Extract YYYY-MM-DD from various date string formats
const parseDate = (dateStr: string): string => {
  const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : dateStr.split(' ')[0];
};

// Extract fault entries from either array [{name,value}] or object {fault: ms} format
const extractFaults = (faults: any): { fault: string; duration: number }[] => {
  if (Array.isArray(faults)) {
    return faults.map((item: any) => ({
      fault: item.name || 'Unknown',
      duration: typeof item.value === 'number' ? item.value : 0
    }));
  }
  if (faults && typeof faults === 'object') {
    return Object.entries(faults).map(([fault, duration]) => ({
      fault,
      duration: typeof duration === 'number' ? duration : 0
    }));
  }
  return [];
};

const DowntimeDurationOfFaultsChart: React.FC<DowntimeDurationOfFaultsChartProps> = ({ 
  machineSerialNo, 
  shifts = []
}) => {
  const { t } = useTranslation();
  const { token } = useUnifiedAuth();
  const { dateRange } = useSAPDataStore();
  const [rawChartData, setRawChartData] = useState<DowntimeDurationDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const chartCache = useChartCache<DowntimeDurationResults>({
    maxAge: 5 * 60 * 1000,
    maxEntries: 30
  });

  const fetchDowntimeDurationData = async () => {
    if (!shifts || shifts.length === 0) {
      setRawChartData([]);
      setLoading(false);
      return;
    }
    if (!machineSerialNo || !dateRange.start || !dateRange.end) {
      setLoading(false);
      return;
    }
    if (!token) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    const cachedData = chartCache.getCachedData(
      'pc.downtime_duration_of_faults',
      machineSerialNo,
      dateRange,
      shifts
    );

    if (cachedData) {
      processData(cachedData);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = ChartDataService.buildRequestParams(machineSerialNo, dateRange, shifts);
      const response = await ChartDataService.fetchDowntimeDurationOfFaults(params, token);

      if (response.success && response.data) {
        const responseData = response.data as any;
        const results = responseData.results || responseData;
        
        chartCache.setCachedData('pc.downtime_duration_of_faults', machineSerialNo, dateRange, shifts, results);
        processData(results);
      } else {
        setError('Failed to fetch downtime duration of faults data');
      }
    } catch (error) {
      console.error('Error fetching downtime duration of faults data:', error);
      setError('Error fetching downtime duration of faults data');
    } finally {
      setLoading(false);
    }
  };

  const processData = (data: DowntimeDurationResults) => {
    const processedData: DowntimeDurationDataPoint[] = [];

    if (!data || typeof data !== 'object') {
      setRawChartData([]);
      return;
    }

    const entries = Object.entries(data);
    if (entries.length === 0) {
      setRawChartData([]);
      return;
    }

    // Detect nested format: { date: { shift: faults } }
    // vs flat format: { "date shift": faults }
    const [, firstValue] = entries[0];
    const isNested = firstValue && typeof firstValue === 'object' && !Array.isArray(firstValue) &&
      Object.values(firstValue).some((v: any) =>
        (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object' && 'name' in v[0]) ||
        (typeof v === 'object' && v !== null && !Array.isArray(v) && Object.values(v).some((sv: any) => typeof sv === 'number'))
      );

    if (isNested) {
      // { "date_timestamp": { "shift": { "fault": ms } | [{name,value}] } }
      for (const [dateKey, shiftsData] of entries) {
        if (!shiftsData || typeof shiftsData !== 'object') continue;
        const displayDate = parseDate(dateKey);
        for (const [shift, faults] of Object.entries(shiftsData as Record<string, any>)) {
          for (const { fault, duration } of extractFaults(faults)) {
            processedData.push({ date: displayDate, shift, fault, duration });
          }
        }
      }
    } else {
      // { "date shift": { fault: ms } | [{name,value}] }
      for (const [key, faults] of entries) {
        // Parse "2015-01-20 B1" or similar
        const spaceIdx = key.indexOf(' ');
        const date = spaceIdx > 0 ? key.substring(0, spaceIdx) : key;
        const shift = spaceIdx > 0 ? key.substring(spaceIdx + 1) : '';
        const displayDate = parseDate(date);
        for (const { fault, duration } of extractFaults(faults)) {
          processedData.push({ date: displayDate, shift, fault, duration });
        }
      }
    }

    setRawChartData(processedData);
  };

  useEffect(() => {
    fetchDowntimeDurationData();
  }, [machineSerialNo, dateRange.start, dateRange.end, shifts, token]);

  // Transform data into stacked column chart format (like NumberOfRejectEventsChart)
  const chartData = useMemo(() => {
    if (!rawChartData || rawChartData.length === 0) {
      return { categories: [] as string[], series: [] as { name: string; data: number[] }[] };
    }

    // Build categories (date + shift) sorted by date then shift
    const categorySet = new Map<string, { date: string; shift: string }>();
    const faultTotals = new Map<string, number>();

    for (const d of rawChartData) {
      const cat = `${d.date} ${d.shift}`.trim();
      if (!categorySet.has(cat)) {
        categorySet.set(cat, { date: d.date, shift: d.shift });
      }
      faultTotals.set(d.fault, (faultTotals.get(d.fault) || 0) + d.duration);
    }

    const sortedCategories = Array.from(categorySet.entries())
      .sort(([, a], [, b]) => a.date.localeCompare(b.date) || a.shift.localeCompare(b.shift));

    const categories = sortedCategories.map(([cat]) => cat);

    // Sort faults by total duration descending, take top 10
    const sortedFaults = Array.from(faultTotals.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([fault]) => fault);

    // Build data matrix: for each fault, an array of duration values per category
    const dataByCategory = new Map<string, Map<string, number>>();
    for (const cat of categories) {
      dataByCategory.set(cat, new Map());
    }
    for (const d of rawChartData) {
      const cat = `${d.date} ${d.shift}`.trim();
      const catMap = dataByCategory.get(cat);
      if (catMap && sortedFaults.includes(d.fault)) {
        catMap.set(d.fault, (catMap.get(d.fault) || 0) + d.duration);
      }
    }

    const series = sortedFaults.map(fault => ({
      name: fault,
      data: categories.map(cat => dataByCategory.get(cat)?.get(fault) || 0)
    }));

    return { categories, series };
  }, [rawChartData]);

  const hasData = chartData.series.length > 0 && chartData.categories.length > 0;

  if (loading) {
    return (
      <div className="w-full">
        <BaseChart
          type="column"
          height={400}
          categories={[]}
          series={[]}
          loading={true}
        />
      </div>
    );
  }

  if (!shifts || shifts.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-lg font-semibold theme-text-primary mb-2">{t('charts.noShiftsSelected', 'No Shifts Selected')}</h3>
          <p className="theme-text-secondary">{t('charts.noShiftsMessage', 'No shifts selected or available for the selected timeframe')}</p>
        </div>
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-lg font-semibold theme-text-primary mb-2">No Downtime Duration Data</h3>
          <p className="theme-text-secondary">No fault downtime duration data found for the selected period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <BaseChart
        commentProps={{ chartType: 'downtime_duration_of_faults', machineBK: machineSerialNo }}
        type="column"
        height={400}
        categories={chartData.categories}
        series={chartData.series}
        yAxis={{
          title: t('charts.duration', 'Duration'),
          min: 0
        }}
        customOptions={{
          yAxis: {
            stackLabels: {
              enabled: true,
              style: { fontWeight: 'bold', color: 'gray' },
              formatter: function(this: any) {
                return formatDuration(this.total);
              }
            },
            labels: {
              formatter: function(this: any) {
                return formatDurationAxis(this.value);
              }
            }
          },
          plotOptions: {
            column: {
              stacking: 'normal',
              dataLabels: { enabled: false }
            }
          },
          xAxis: {
            labels: {
              rotation: -45,
              style: { fontSize: '10px' }
            }
          },
          tooltip: {
            formatter: function(this: any) {
              const point = this.point;
              const total = this.total || 0;
              const category = (point as any).category ||
                               (this.series.xAxis?.categories && this.series.xAxis.categories[point.x as number]) ||
                               String(this.x ?? '');

              const parts = category.split(' ');
              const date = parts[0] || '';
              const shift = parts[1] || '';

              return `<b>Fault:</b> ${this.series.name}<br/>` +
                     `<b>Duration:</b> ${formatDuration(point.y ?? 0)}<br/>` +
                     `<b>Total:</b> ${formatDuration(total)}<br/>` +
                     `<b>Shift:</b> ${shift}<br/>` +
                     `<b>Date:</b> ${date}`;
            }
          },
          legend: {
            enabled: true,
            layout: 'vertical',
            align: 'right',
            verticalAlign: 'middle',
            itemStyle: { fontSize: '11px' },
            maxHeight: 350
          }
        }}
        legend={{ enabled: true }}
        loading={loading}
        error={error}
        onRetry={fetchDowntimeDurationData}
        className="w-full"
      />
    </div>
  );
};

export default DowntimeDurationOfFaultsChart;
